<script>
export default {
  name: "HomeView",
};
</script>

<script setup>
// La portada.
//
// Figma: Template/Home-Reset, tablero claro 730:7237 (y su gemelo oscuro en
// 659:2861). Prototipo: docs/NEW UI/astra-prototipo-24-08.html, pantalla Home.
//
// Orden de la plantilla: hero · tres puertas · programación de hoy ·
// actualidad. La cabecera y el pie los pinta App.vue para todas las vistas del
// reset, así que aquí no se montan.
//
// El hero NO es el de Radio aunque comparta componente: la pila va SIN la
// portada encajada. Llevó una tercera columna con la ficha del artista —el
// nodo la tiene— y **se retiró el 26.08.26 por decisión de Pablo**.
// `ArtistAside` sigue en el sistema, sin consumidores, por si vuelve con la
// ficha «Con Astra» de `_3`.
//
// ⚠️ La pila se queda en **400**, la medida de siempre. Al retirar la ficha se
// subió a 500 por iniciativa propia y no se había pedido: revertido.
//
// ⚠️ La imagen es la del ARTISTA, no la carátula del álbum. Es lo que ya hacía
// la portada anterior y lo que pide el diseño; la carátula solo aparece como
// dato en el pie de foto. Corregido el 25.08.26 sobre la primera lectura del
// nodo.
//
// ⚠️ La capa de datos NO cambia: mismos composables que Radio. La bio y la
// imagen del artista vienen de la cadena de docs/ARTISTAS_FUENTES.md, no de
// WordPress; de WordPress sale solo la parrilla.
//
// 🚧 En construcción (C4 · Home): H2 deja hero y pie. Las tres puertas, la
// parrilla del día y actualidad llegan en H3 y H4.
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useHead } from "@vueuse/head";

import { getTodayShows, getRadioNowMinutes } from "@/services/daySchedule";
import { getLatestVideo } from "@/services/tvVideos";
import { getEntradas } from "@/services/canalAbierto";
import { getAgenda } from "@/services/conciertos";
import { fetchRadar } from "@/services/api";
import { getAvisosActualidad } from "@/services/avisos";
import { olvidarContenido } from "@/services/refrescoContenido";
import { formatRange } from "@/utils/timeUtils";
import { resetCircuitBreaker } from "@/services/fetchWithRetry";

import ButtonDs from "@/components/ds/ButtonDs.vue";
import HeroAgenda from "@/components/ds/HeroAgenda.vue";
import DoorCard from "@/components/ds/DoorCard.vue";
import VideoFacade from "@/components/ds/VideoFacade.vue";
import { useVideoExclusivo } from "@/composables/useVideoExclusivo";
import DayBar from "@/components/ds/DayBar.vue";
import DataRow from "@/components/ds/DataRow.vue";
import ModuleTitleBar from "@/components/ds/ModuleTitleBar.vue";
import GridCell from "@/components/ds/GridCell.vue";
import SendRecord from "@/components/ds/SendRecord.vue";
import ArtCarousel from "@/components/ds/ArtCarousel.vue";
import CornerTag from "@/components/ds/CornerTag.vue";
import { LISTEN } from "@/services/host";
import { generosDe } from "@/services/programmes";

const STATION_ID = "3";

// ⛔ **La portada ya no lleva lo que suena** (21.10.26): la columna de emisión
// salió del hero y va en la cabecera, que monta `App.vue`. Aquí estaban
// `useNowPlaying`, `useCurrentProgramme`, la onda, el reloj del tema, el retrato
// que se encendía con el play, el transporte y el compartir.

const todayShows = ref([]);
const nowMinutes = ref(getRadioNowMinutes());
const latestVideo = ref(null);
const releases = ref([]);
// Lo último que ha entrado por el buzón, para la puerta de Radar.
const ultimaEntrada = ref(null);
const releaseIndex = ref(0);
const noticias = ref([]);

// ── Lo que suena ──

// 🔴 Aquí había un `album` que iba de pie sobre la foto del artista, y se
// retira (encargo de Pablo, 27.08.26): la imagen es del ARTISTA, y colgarle
// encima el título del disco la convierte en la portada de otra cosa. El álbum
// no se pierde de la web —sigue en Radio, donde la carátula sí está encajada
// junto a la foto y el pie explica qué se está viendo—.
//
// El kicker de la portada tampoco dice el álbum: dice de qué programa viene lo
// que suena y de qué palo es.
// 🔴 **El género es el del PROGRAMA, y viene del servidor.** Hasta el 22.09.26
// aquí se pintaba `artistFacts.genre`, que es el género del artista que suena y
// **sale de TheAudioDB**, una base externa: cambiaba con cada canción, no lo
// controlamos nadie y decía «Alternative Rock» mientras el programa era Alta
// Fidelidad —Indie, Rock Clásico, New Romantic—. Lo cazó Pablo: «debe mostrar
// los géneros que obtenemos desde el servidor».
//
// ⚠️ El género del artista sigue estando donde le toca, en su ficha de Radio.
// Lo que no puede es hacerse pasar por el del programa.
//
// Los une `generosDe`, que es el único sitio donde se decide cómo se escribe
// una lista de géneros.
// 🔴 **Dos líneas, no una.** Pablo, 22.09.26: arriba el programa y debajo su
// franja y sus géneros. En una sola línea los tres datos se leían como una
// ristra —«ALTA FIDELIDAD · 12–16 · INDIE, ROCK CLÁSICO, NEW ROMANTIC»— y el
// nombre del programa, que es lo que se busca, no destacaba sobre lo demás.

// ── La parrilla del día ──
//
// La regla de horas y la lista salen de la misma consulta: cada franja pesa lo
// que dura su programa, así que la barra necesita los mismos minutos que las
// filas. Pedirlo dos veces daría dos verdades cuando cambie el programa entre
// una llamada y otra.
const daySlots = computed(() =>
  todayShows.value.map((show) => ({
    // La regla es estrecha: cabe la hora de entrada, no el nombre.
    label: String(Math.floor(show.start)).padStart(2, "0"),
    start: show.startMin,
    end: show.endMin,
    live: show.live,
  })),
);

const dayRows = computed(() =>
  todayShows.value.map((show) => ({
    time: formatRange(show.range),
    name: show.name,
    meta: generosDe(show),
    resident: show.resident,
    tail: show.live ? "En antena" : "",
    tailAccent: show.live,
    live: show.live,
  })),
);

const nowLabel = computed(() => {
  const m = nowMinutes.value % (24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
});

// ── Las tres puertas ──
//
// Una puerta sin datos NO se pinta. Es la regla del sistema para las celdas y
// vale igual aquí: tres puertas de las que una anuncia un vídeo que no existe
// valen menos que dos puertas ciertas.
// 🔴 Los campos son los que normaliza `services/tvVideos.js`, no los del
// contrato: `thumbnail` y no `thumbnail_url`. Desde el 26.08 los vídeos salen
// de `tv.json` del servidor de la radio y no del manager.
//
// `duration` ya no se pide: el contrato lo publica vacío o nulo en los treinta
// y nueve vídeos, así que ese hueco del meta nunca se llenaba.
const tvDoor = computed(() => {
  const v = latestVideo.value;
  if (!v) return null;
  return {
    // Para la salida a la sección: `/butaca/videoclip/<slug>` abre su página.
    // Desde el 21.09.26 la tarjeta ya no carga el vídeo en el hero: cada pieza
    // tiene dirección propia (entrega de Butaca).
    slug: v.slug || "",
    title: v.name || "Última sesión",
    url: v.url || "",
    image: v.thumbnail || "",
    imageFallback: v.thumbnailFallback || "",
    meta: [v.playlist, "Al reproducir, la radio se pausa"].filter(Boolean),
  };
});

// La emisión se para al arrancar un vídeo. No pueden sonar las dos cosas.
// Misma regla que en la sección TV, mismo cable: ver `useVideoExclusivo`.
const videoPuerta = ref(null);
const { alReproducirVideo, alPausarVideo } = useVideoExclusivo(videoPuerta);

// La puerta del buzón tiene dos caras, y la que toca depende de si hay algo
// que enseñar:
//
//   · con entradas → el último disco recibido, con su portada. Es la prueba de
//     que el buzón funciona, que convence más que cualquier frase.
//   · sin entradas → qué es la sección. **No se inventa una entrada** ni se
//     deja la puerta a medias con un hueco de imagen vacío.
const canalDoor = computed(() => {
  const e = ultimaEntrada.value;

  if (!e) {
    return {
      title: "Manda tu disco",
      meta: ["Sin sello, sin nota de prensa", "Lo escuchamos entero"],
      image: "",
      cta: "Cómo mandarlo",
      to: "/radar",
    };
  }

  return {
    title: `${e.artist} · ${e.title}`,
    // Lo que sitúa el disco. Se salta lo que falte: son opcionales de verdad.
    meta: [e.place, e.formatLabel, e.onAir ? "En parrilla" : ""].filter(Boolean),
    image: e.cover,
    // 🔴 A la **ficha del artista**, que es donde Radar enseña hoy lo que llega
    // por el buzón. Apuntaba a `/radar/<slug>`, la vista de detalle por trabajo
    // del diseño anterior, que se quedó sin rehacer cuando el escaparate pasó a
    // ser por artista: desde la portada se aterrizaba en la página vieja.
    cta: "Ver la ficha",
    to: `/radar/artista/${e.slug}`,
  };
});

// La puerta de lanzamientos sale de los posts de la categoría «lanzamientos»
// del manager, que es de donde ya salía el carrusel de la portada anterior.
// `useNewAlbums.js` apunta a la playlist «Nuevas Adiciones» de AzuraCast y no
// lo usa nadie: está vivo pero desconectado. Anotado en REFACTOR.md para
// decidir cuál manda.
//
// Pasan solas, sin flechas ni puntos, y son las que mande el servidor: hoy ocho,
// todo lo publicado en su ventana de 30 días. Antes se recortaban a cuatro y la
// mitad del mes no llegaba a verse.
//
// El texto de la puerta va atado a la que se ve: un título quieto sobre una
// imagen que cambia se lee como un fallo de sincronía, no como un carrusel.
const releaseArt = computed(() =>
  releases.value.map((post) => post.imagen).filter(Boolean),
);

const releaseDoor = computed(() => {
  const post = releases.value[releaseIndex.value] || releases.value[0];
  if (!post) return null;
  return {
    title: post.titulo || "",
    meta: [post.categoria, post.fecha].filter(Boolean),
    to: post.slug ? `/actualidad/${post.slug}` : null,
  };
});

// ── Emisión ──
// El análisis arranca DENTRO del gesto: el AudioContext nace suspendido y solo
// se reanuda en una interacción del usuario.

// Lo que se dice al compartir. El enlace lo pone el propio popover: en la
// portada es la dirección de la casa, no la de una ficha.

// ── Ciclo ──
let showTimer = null;
let relojTimer = null;

async function loadDay() {
  try {
    todayShows.value = await getTodayShows(null, STATION_ID);
    nowMinutes.value = getRadioNowMinutes();
  } catch (error) {
    // Sin parrilla la sección entera no se pinta: media parrilla engaña más
    // que ninguna.
    todayShows.value = [];
  }
}

async function loadDoors() {
  // En paralelo y con fallos independientes: que TV no responda no puede
  // dejar sin lanzamientos a la portada.
  const [video, lanzamientos, canal] = await Promise.all([
    getLatestVideo(),
    // 🔴 Del endpoint de avisos, y no de un filtro nuestro: el servidor publica
    // los últimos 30 días ya ordenados y filtrados. Antes esto recortaba a
    // siete días sobre `actualidad.json` entero, y con esa ventana la puerta
    // llegó a quedarse con un solo disco.
    getAvisosActualidad().catch(() => []),
    getEntradas().catch(() => []),
  ]);
  latestVideo.value = video;
  // 🔴 **Solo lanzamientos.** `?fuente=actualidad` trae todos los posts de
  // Actualidad —lanzamientos, noticias e historias—, y la puerta es «Nuevas
  // realizaciones»: el 04.10.26 se coló la historia de Córdoba entre los discos
  // (lo vio Pablo). Se filtra por el `tipo` del aviso, que es el del contrato.
  releases.value = (lanzamientos || []).filter((a) => a.tipo === "lanzamiento");
  ultimaEntrada.value = (canal || [])[0] || null;
}

// La API devuelve `categorias` —un array de objetos con id, nombre y slug— y no
// un `categoria` de texto. Leerlo como cadena dejaba el kicker vacío en el
// destacado y en las tres breves, sin dar ningún error.
//
// `imagen` es la apaisada del servidor —800×600, unos 80 KB—, ya lista para el
// hueco. Antes era el archivo completo de WordPress y había que derivarla.
function normalizaPost(post) {
  if (!post) return null;
  return {
    ...post,
    categoria: post.categorias?.[0]?.nombre || "",
  };
}

// ── La agenda del hero ──
//
// Contrato de conciertos del 01.09.26. Vacía es una respuesta normal —«la agenda
// puede estar vacía o casi al principio»—, y entonces el hero se queda con la
// emisión a todo lo ancho, que es lo que había hasta hoy.
const agenda = ref([]);

async function loadAgenda() {
  agenda.value = await getAgenda();
}

// Los tipos que van en el módulo Actualidad de la portada.
const TIPOS_ACTUALIDAD = ["noticias", "historias"];

async function loadNews() {
  // Cuatro iguales, en orden. Antes se partían en destacado + tres breves; el
  // reparto se fue el 18.09.26.
  //
  // 🔴 **Noticias e historias, lo último primero, de izquierda a derecha**
  // (Pablo, 04.10.26). Hasta ese día solo se pedían noticias, y una historia
  // como la de Córdoba no llegaba nunca a la portada. Los lanzamientos no van
  // aquí: tienen su puerta, «Nuevas realizaciones».
  //
  // El orden es el de `getActualidad`, del más nuevo al más viejo por fecha y
  // hora de publicación; aquí solo se filtra y se cortan cuatro.
  const posts = await fetchRadar(0).catch(() => []);
  noticias.value = posts
    .filter((p) => TIPOS_ACTUALIDAD.includes(p.categorias?.[0]?.slug))
    .slice(0, 4)
    .map(normalizaPost)
    .filter(Boolean);
}

// Los cortacircuitos se resetean al montar. `fetchWithRetry` bloquea un host
// tras varios fallos seguidos; si se llega a la portada con el circuito
// abierto, la vista se queda en esqueleto para siempre y nada lo explica.
function unblock() {
  resetCircuitBreaker(`${LISTEN}`);
  resetCircuitBreaker("https://astra.fm");
  resetCircuitBreaker("https://www.theaudiodb.com");
}

function startClock() {
  if (relojTimer) return;
  relojTimer = setInterval(() => {
    // La aguja de la regla también corre. En minutos, así que el cálculo solo
    // cambia de verdad una vez por minuto.
    nowMinutes.value = getRadioNowMinutes();
  }, 1000);
}

function startPolling() {
  stopPolling();
  startClock();
  // El programa lo lleva su composable, que se relee al terminar la franja. La
  // parrilla se queda con su red de seguridad: sus filas solo cambian si
  // alguien edita la programación en WordPress.
  showTimer = setInterval(loadDay, 5 * 60 * 1000);
}

function stopPolling() {
  [showTimer, relojTimer].forEach((t) => t && clearInterval(t));
  showTimer = relojTimer = null;
}

// En segundo plano no se pide nada: la pestaña oculta no la mira nadie y el
// navegador estrangula los timers igualmente.
function onVisibility() {
  if (document.hidden) {
    stopPolling();
    return;
  }
  unblock();
  // Volver de segundo plano es el caso en el que más fácil es que el programa
  // haya cambiado: los temporizadores estaban parados. Se relee entero.
  loadDay();
  startPolling();

  // 🔴 Y lo mismo vale para lo que publica la redacción.
  //
  // Las puertas y la actualidad se pedían UNA vez, al montar, y nunca más:
  // medido el 27.08 con la portada abierta y quieta, una sola petición a cada
  // JSON en tres minutos. Con `keep-alive` la vista tampoco se remonta al
  // volver de otra sección, así que quien dejaba la web abierta no veía un
  // artículo nuevo jamás — había que recargar, y ni recargando, porque el
  // navegador contestaba de su caché.
  //
  // Volver a la pestaña es el momento honesto para preguntar: ya existe el
  // aviso, no cuesta un sondeo periódico y es cuando más probable es que algo
  // haya cambiado. Se olvida lo guardado ANTES de pedir, o se volvería a
  // servir lo mismo desde la memoria del módulo.
  //
  // No bloquea nada: la portada sigue pintada con lo que ya tenía y solo se
  // repinta lo que de verdad llegue distinto.
  olvidarContenido();
  loadDoors();
  loadNews();
  loadAgenda();
}

onMounted(async () => {
  startClock();
  unblock();
  document.addEventListener("visibilitychange", onVisibility);
  // Lo de arriba primero. Las puertas y la actualidad están por debajo del
  // pliegue y pueden llegar después; el hero no.

  // 🔴 Pero «después» no es «cuando termine el hero». Estaban DEBAJO del
  // `await`, así que no salían a pedir sus datos hasta que el now playing
  // resolviera: medido el 26.08, unos veinte segundos con la portada a medias
  // —sin puertas y sin actualidad— aunque su JSON estuviera a mano. Se lanzan
  // ya, sin esperar; cada una pinta lo suyo cuando llega.
  loadDoors();
  loadNews();
  // La agenda comparte fila con el hero, así que se pide con ellos y no con lo
  // de abajo; pero tampoco espera al now playing: si tarda, el hero se pinta
  // solo con la emisión y la agenda entra cuando llega.
  loadAgenda();

  await loadDay();
  startPolling();
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", onVisibility);
  stopPolling();
});

useHead({
  title: "Astra FM · radio independiente",
  meta: [
    {
      name: "description",
      content:
        "Radio independiente. Rock y lo que se le parece, veinticuatro horas, sin listas compradas.",
    },
  ],
});
</script>

<template>
  <div class="home-view">
    <!-- 1 · el hero: la agenda en franja ──────────────────────────────
         🔴 **Desde el 21.10.26 el hero es solo la agenda**, de lado a lado
         (`hero · agenda en franja 2586:14920`, entrega
         `2026-10-21-portada-franja-y-player-en-cabecera.md`). La columna de
         emisión —foto del artista, velo, transporte— sale de la portada: lo
         que suena va en la cabecera, en todas las pantallas. Sin conciertos, no
         hay hero y la portada empieza por las puertas. -->
    <HeroAgenda v-if="agenda.length" :conciertos="agenda" />

    <!-- 2 · las tres puertas -->
    <section
      v-if="tvDoor || releaseDoor"
      class="home-view__doors"
      aria-label="Secciones destacadas"
    >
      <!-- 🔴 **Sin `to`.** El contrato del servidor lo prohíbe expresamente:
           «en la HOME el click NUNCA navega a la sección TV». La tarjeta no es
           un enlace — el vídeo se reproduce aquí mismo y se amplía desde el
           propio reproductor. El enlace a la sección vive en el menú. -->
      <!-- La salida a la sección va FUERA de la tarjeta, y por dos razones.
           La primera es de marcado: esta puerta se renderiza como `<button>`
           —no navega, reproduce— y un enlace dentro de un botón es contenido
           interactivo anidado. La segunda es de comportamiento: siendo hermano
           no le quita ni un toque al play, así que el vídeo se sigue
           reproduciendo AQUÍ, que es lo que exige el contrato. Lo que lleva a
           la sección es este enlace, y solo él. -->
      <div v-if="tvDoor" class="home-view__tv">
        <DoorCard
          badge="Último videoclip"
          :title="tvDoor.title"
          :meta="tvDoor.meta"
        >
          <template #art>
            <VideoFacade
              ref="videoPuerta"
              :url="tvDoor.url"
              :poster="tvDoor.image"
              :poster-fallback="tvDoor.imageFallback"
              :title="tvDoor.title"
              tag="Último videoclip"
              hover-color
              @play="alReproducirVideo"
              @pause="alPausarVideo"
            />
          </template>
        </DoorCard>

        <!-- La misma llamada que la puerta, con el botón del sistema: aquí
             vivía una copia de su CSS, y era uno de los cinco sitios donde
             había que repetir cada cambio. -->
        <ButtonDs
          v-if="tvDoor.slug"
          class="home-view__tv-cta"
          label="Ver Butaca"
          variant="quiet"
          size="micro"
          :to="`/butaca/videoclip/${tvDoor.slug}`"
        />
      </div>

      <DoorCard
        v-if="releaseDoor"
        badge="Nuevas realizaciones"
        :title="releaseDoor.title"
        :meta="releaseDoor.meta"
        cta="Ver lanzamientos"
        :to="releaseDoor.to || '/actualidad'"
      >
        <template #art>
          <ArtCarousel
            :images="releaseArt"
            @change="releaseIndex = $event"
          />
          <CornerTag anchor="top">Nuevas realizaciones</CornerTag>
        </template>
      </DoorCard>

      <!-- **Radar** —«Canal abierto» hasta el 31.08.26—. Enseña lo ÚLTIMO que
           ha entrado por el buzón, y
           mientras no haya nada publicado dice lo que la sección es. Nunca un
           disco inventado: por eso se fue `src/data/demos.js`, una de las tres
           deudas que impedían desplegar. Ver docs/CANAL_ABIERTO.md. -->
      <DoorCard
        badge="Radar"
        :title="canalDoor.title"
        :meta="canalDoor.meta"
        :image="canalDoor.image"
        :cta="canalDoor.cta"
        :to="canalDoor.to"
      />
    </section>

    <!-- 3 · la parrilla del día -->
    <section v-if="dayRows.length" aria-label="Programación de hoy">
      <ModuleTitleBar
        title="Programación de hoy"
        end="action"
        action-text="Ver semana completa"
        action-to="/radio/shows"
      />

      <DayBar :slots="daySlots" :now="nowMinutes" :now-label="nowLabel" />

      <DataRow
        v-for="row in dayRows"
        :key="row.time + row.name"
        variant="sched"
        v-bind="row"
      />
    </section>

    <!-- 4 · actualidad
         Barra de título y cuatro celdas en fila, como «Programación de hoy»
         justo encima. Entrega
         `docs/entregas/2026-09-18-actualidad-en-la-portada.md`: era el único
         módulo de la portada que no decía cómo se llamaba, y la portada no
         tenía puerta al archivo de noticias. -->
    <section v-if="noticias.length" aria-label="Actualidad">
      <ModuleTitleBar
        title="Actualidad"
        end="action"
        action-text="Ver todo"
        action-to="/actualidad"
      />

      <div class="home-view__noticias">
        <GridCell
          v-for="post in noticias"
          :key="post.slug"
          :kicker="post.categoria"
          :kicker-end="post.fechaCorta"
          :title="post.titulo"
          :image="post.imagen"
          :to="`/actualidad/${post.slug}`"
        />
      </div>
    </section>

    <!-- 5 · el buzón, el único bloque invertido de la página.
         Va aquí —debajo de actualidad y antes del pie—, que es donde lo pone
         `Template/Home-Reset`. Estuvo un día en Radio por decisión de Pablo y
         vuelve a la portada el 26.08.26.

         Textos fijos a propósito: no hay formulario ni fuente detrás, y la
         llamada es un `mailto:` hasta que lo haya. Es contenido de la casa, no
         un dato inventado: aquí no hay nada que pueda leerse como información
         falsa. -->
    <!-- Sin props: el texto del buzón vive en el componente, porque es el
         mismo aquí y en «Sobre Astra FM». -->
    <!-- Adelanto: la llamada lleva a la sección, que es donde se explica
         y donde está el formulario. El `mailto` vive allí. -->
    <SendRecord cta="Cómo mandarlo" cta-to="/radar" />

  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/estructura" as e;
@use "@/assets/scss/type" as t;

.home-view {
  // 🔴 **Las juntas entre módulos las pone la pila, no cada módulo.**
  // `scss/_estructura.scss`. Con esto ningún módulo lleva filete de cierre: si
  // hay dos piezas hay una línea, y si no la hay, no.
  @include e.pila;
  background: var(--color-bg-canvas);
}

// ── Las tres puertas ──
// Filete entre ellas y ninguno al final: el borde del módulo lo pone la línea
// inferior de la sección.
.home-view__doors {
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  > * {
    border-right: 1px solid var(--color-border-default);

    &:last-child {
      border-right: 0;
    }
  }
}

// ── Actualidad ──
//
// Cuatro celdas en fila, con la misma gramática de filetes que el resto de las
// retículas de la casa: cada celda dibuja el suyo a la derecha y el módulo
// cierra abajo con la línea fuerte. El `padding-left` de 1 evita la línea doble
// contra el borde de la página.
//
// 🔴 **Sin escalón de tres columnas.** Con cuatro piezas, tres columnas dejan
// una segunda fila con una celda suelta y dos huecos. Decidido con diseño el
// 18.09.26: de cuatro se pasa a dos.
.home-view__noticias {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  @include e.juntas-de-rejilla;

  > * {
    border-right: 1px solid var(--color-border-default);

    &:last-child {
      border-right: 0;
    }
  }
}

@media (max-width: 1080px) {
  .home-view__noticias {
    grid-template-columns: repeat(2, 1fr);
  }

  // A dos columnas la segunda de cada fila se queda sin vecina a la derecha.
  .home-view__noticias > *:nth-child(2n) {
    border-right: 0;
  }
}

@media (max-width: 720px) {
  .home-view__noticias {
    grid-template-columns: 1fr;
  }

  .home-view__noticias > * {
    border-right: 0;
  }
}

// ── La puerta de TV y su salida ──
//
// La tarjeta se queda con todo el alto y el enlace se descuelga debajo, en el
// margen del cuerpo, así que cae en la esquina inferior derecha de la puerta
// sin taparle nada.
.home-view__tv {
  display: flex;
  flex-direction: column;
  min-width: 0;

  .door {
    flex: 1;
  }
}

// La misma llamada que la de las otras dos puertas, hasta el hover que la
// invierte. Los valores se repiten porque `.door__cta` vive en el `<style
// scoped>` de `DoorCard` y no alcanza hasta aquí, y este enlace tiene que estar
// fuera de la tarjeta. Si cambia una, cambian las dos.
//
// Cuelga del mismo sitio que las otras dos, a la izquierda: el margen lateral
// repite el relleno del cuerpo de la tarjeta, así que las tres llamadas de la
// fila arrancan en la misma vertical.
.home-view__tv-cta {
  align-self: flex-start;
  margin: 0 24px 22px;
}

// Se enciende al pasar por la PUERTA entera, no solo por encima del enlace.
//
// Lo pidió Pablo el 28.08.26, y con razón: las otras dos puertas invierten su
// llamada en cuanto el ratón entra en la tarjeta, y esta se quedaba apagada al
// lado. Que sean el mismo componente y se comporten distinto es justo lo que
// se nota.
//
// El reparo que tenía —encenderlo sobre el vídeo promete que la tarjeta lleva
// a la sección— lo resuelve el propio vídeo: pulsarlo reproduce ahí mismo, y eso
// se ve al instante. Lo que no se explicaba era la diferencia.
.home-view__tv:hover .home-view__tv-cta,
.home-view__tv:focus-within .home-view__tv-cta,
.home-view__tv-cta:hover,
.home-view__tv-cta:focus-visible {
  // El hover de la puerta enciende su llamada, con el mismo rojo que el resto
  // de botones de la web.
  border-color: var(--color-text-accent);
  color: var(--color-text-accent);
}

// Por debajo de la retícula ancha las puertas se apilan. El filete que
// separaba columnas pasa a separar filas.
@media (max-width: 980px) {
  .home-view__doors {
    grid-template-columns: 1fr;
  }

  .home-view__doors > * {
    border-right: 0;
    border-bottom: 1px solid var(--color-border-default);
  }
}

</style>
